import React, {useEffect, useMemo, useState} from 'react';

import './styles.scss';
import {Category, categoryOptions, GroupedData} from '../../App';
import {useLocation, useNavigate} from 'react-router-dom';
import Select, {components} from 'react-select';
import SocialIcons from '../SocialIcons';
import {BiChevronUp} from 'react-icons/bi';
import {getFriendlyProductStatus} from '../../utils';
import Icon from '../Icon/Icon';
import ReactSwitch from 'react-switch';

const {Option} = components;
function IconOption(props: any) {
  const {value, label} = props.data;

  return (
    <Option {...props}>
      <div className="priority-option">
        <Icon type={value} width={22} />
        <span>{label}</span>
      </div>
    </Option>
  );
}

interface FilterBarProps {
  disabled: boolean;
  searchValue: string;
  setSearchValue: (code: string) => void;
  groupedData: GroupedData;
  hideResults?: boolean;
  setFilteredCity: (city: string | null) => void;
  setHoveredCity: (city: string | null) => void;
  filterProductionStatus?: string[];
  setFilterProductionStatus?: (status: string[]) => void;
  filteredCountry?: string[];
  setFilteredCountry?: (country: string[]) => void;
  filteredInvestorCountry?: string[];
  setFilteredInvestorCountry?: (country: string[]) => void;
  filteredCategory?: string[];
  setFilteredCategory?: (category: string[]) => void;
  hideMap?: boolean;
  setHideMap?: (hideMap: boolean) => void;
}

const FilterBar = ({
  disabled = false,
  searchValue,
  setSearchValue,
  groupedData,
  hideResults = false,
  setFilteredCity,
  setHoveredCity,
  filterProductionStatus = [],
  setFilterProductionStatus,
  filteredCountry = [],
  setFilteredCountry,
  filteredInvestorCountry = [],
  setFilteredInvestorCountry,
  filteredCategory = [],
  setFilteredCategory,
  hideMap,
  setHideMap,
}: FilterBarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [filtersOpen, setFiltersOpen] = useState(false);

  const toggleFilters = () => {
    setFiltersOpen(!filtersOpen);
  };

  const countryOptions = useMemo(() => {
    const countries = new Set<string>([
      'Poland',
      'Hungary',
      'Slovakia',
      'Czech Republic',
    ]);

    return [
      ...Array.from(countries).map((country) => ({
        value: country,
        label: country,
      })),
    ];
  }, [groupedData]);

  const categoryOptionsMap = useMemo(() => {
    return categoryOptions.reduce((acc: Record<Category, string>, category) => {
      acc[category.value] = category.label;
      return acc;
    }, {} as Record<Category, string>);
  }, [categoryOptions]);

  const statusOptions = useMemo(() => {
    return [
      {
        value: 'live',
        label: getFriendlyProductStatus('live'),
      },
      {
        value: 'future',
        label: getFriendlyProductStatus('future'),
      },
    ];
  }, []);

  const statusOptionsMap = useMemo(() => {
    return statusOptions.reduce((acc: Record<string, string>, status) => {
      acc[status.value] = status.label;
      return acc;
    }, {} as Record<string, string>);
  }, [statusOptions]);

  useEffect(() => {
    // Reset search value on URL change
    setSearchValue('');
  }, [location.pathname]);

  const investorCountryOptions = useMemo(() => {
    const investorCountries = new Set<string>(['South Korea', 'China', 'Japan']);

    return [
      ...Array.from(investorCountries).map((country) => ({
        value: country,
        label: country,
      })),
    ];
  }, [groupedData]);

  const clearAllFilters = () => {
    setFilteredCountry?.([]);
    setFilteredInvestorCountry?.([]);
    setFilteredCategory?.([]);
    setFilterProductionStatus?.([]);
  };

  const hasFilters =
    !disabled &&
    (filteredCountry.length > 0 ||
      filteredInvestorCountry.length > 0 ||
      filteredCategory.length > 0 ||
      filterProductionStatus.length > 0);

  return (
    <div className="FilterBar">
      <button
        onClick={() => toggleFilters()}
        className="filters-button"
        disabled={disabled}
      >
        <svg
          height="16"
          viewBox="0 0 28 28"
          width="16"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            clipRule="evenodd"
            d="M4.40675 7.25H3C2.44772 7.25 2 6.80228 2 6.25C2 5.69772 2.44772 5.25 3 5.25H4.40675C4.82853 3.94437 6.05398 3 7.5 3C8.94602 3 10.1715 3.94437 10.5933 5.25H25C25.5523 5.25 26 5.69772 26 6.25C26 6.80228 25.5523 7.25 25 7.25H10.5933C10.1715 8.55563 8.94602 9.5 7.5 9.5C6.05398 9.5 4.82853 8.55563 4.40675 7.25ZM5.75 6.25C5.75 5.2835 6.5335 4.5 7.5 4.5C8.4665 4.5 9.25 5.2835 9.25 6.25C9.25 7.2165 8.4665 8 7.5 8C6.5335 8 5.75 7.2165 5.75 6.25Z"
            fillRule="evenodd"
          />
          <path
            clipRule="evenodd"
            d="M3 15.25H17.4458C17.8676 16.5556 19.093 17.5 20.5391 17.5C21.9851 17.5 23.2105 16.5556 23.6323 15.25H25C25.5523 15.25 26 14.8023 26 14.25C26 13.6977 25.5523 13.25 25 13.25H23.6323C23.2105 11.9444 21.9851 11 20.5391 11C19.093 11 17.8676 11.9444 17.4458 13.25H3C2.44772 13.25 2 13.6977 2 14.25C2 14.8023 2.44772 15.25 3 15.25ZM20.5391 12.5C19.5726 12.5 18.7891 13.2835 18.7891 14.25C18.7891 15.2165 19.5726 16 20.5391 16C21.5056 16 22.2891 15.2165 22.2891 14.25C22.2891 13.2835 21.5056 12.5 20.5391 12.5Z"
            fillRule="evenodd"
          />
          <path
            clipRule="evenodd"
            d="M10.4067 23.25H3C2.44772 23.25 2 22.8023 2 22.25C2 21.6977 2.44772 21.25 3 21.25H10.4067C10.8285 19.9444 12.054 19 13.5 19C14.946 19 16.1715 19.9444 16.5933 21.25H25C25.5523 21.25 26 21.6977 26 22.25C26 22.8023 25.5523 23.25 25 23.25H16.5933C16.1715 24.5556 14.946 25.5 13.5 25.5C12.054 25.5 10.8285 24.5556 10.4067 23.25ZM11.75 22.25C11.75 21.2835 12.5335 20.5 13.5 20.5C14.4665 20.5 15.25 21.2835 15.25 22.25C15.25 23.2165 14.4665 24 13.5 24C12.5335 24 11.75 23.2165 11.75 22.25Z"
            fillRule="evenodd"
          />
        </svg>
        {hasFilters && <span className="filter-indicator" />}
        <span>Filters</span>
      </button>
      <div
        className={`filters-container ${
          filtersOpen && !disabled ? 'open' : ''
        }`}
      >
        <div className="social-buttons">
          <SocialIcons />
        </div>
        <Select
          options={countryOptions}
          placeholder="Country"
          isMulti
          onChange={(selectedOption) => {
            navigate(`/map`);
            setFilteredCountry?.(selectedOption?.map((option) => option.value));
          }}
          isClearable
          value={filteredCountry.map((value) => ({
            value,
            label: value,
          }))}
          styles={{
            control: (baseStyles, state) => ({
              ...baseStyles,
              borderRadius: 8,
              width: '100%',
              fontSize: 14,
            }),
          }}
        />
        <Select
          options={investorCountryOptions}
          placeholder="Investor country"
          isMulti
          onChange={(selectedOption) => {
            navigate(`/map`);
            setFilteredInvestorCountry?.(
                selectedOption.map((option) => option.value),
            );
          }}
          isClearable
          value={filteredInvestorCountry.map((value) => ({
            value,
            label: value,
          }))}
          styles={{
            control: (baseStyles, state) => ({
              ...baseStyles,
              borderRadius: 8,
              width: '100%',
              fontSize: 14,
            }),
          }}
        />
        <Select
          options={statusOptions}
          placeholder="Production status"
          isMulti
          onChange={(selectedOption) => {
            navigate(`/map`);
            setFilterProductionStatus?.(
                selectedOption?.map((option) => option.value),
            );
          }}
          isClearable
          value={filterProductionStatus.map((value) => ({
            value,
            label: statusOptionsMap[value],
          }))}
          styles={{
            control: (baseStyles, state) => ({
              ...baseStyles,
              borderRadius: 8,
              width: '100%',
              fontSize: 14,
            }),
          }}
        />
        <Select
          options={categoryOptions}
          placeholder="Category"
          isMulti
          onChange={(selectedOption) => {
            navigate(`/map`);
            setFilteredCategory?.(
                selectedOption?.map((option) => option.value),
            );
          }}
          isClearable
          components={{
            Option: IconOption,
          }}
          value={filteredCategory.map((value) => ({
            value,
            label: categoryOptionsMap[value as Category],
          }))}
          styles={{
            control: (baseStyles, state) => ({
              ...baseStyles,
              borderRadius: 8,
              width: '100%',
              fontSize: 14,
            }),
          }}
        />
        <div onClick={(e) => e.stopPropagation()} className="toggle-switch">
          <ReactSwitch
            checked={!!hideMap}
            onChange={() => {
              setHideMap(!hideMap);
            }}
            uncheckedIcon={false}
            checkedIcon={false}
            className="switch"
          />
          <label>Show results as list</label>
        </div>
        <button onClick={() => setFiltersOpen(false)} className="hide-filters-button">
          <BiChevronUp />
        </button>
        <button
          onClick={() => clearAllFilters()}
          className="clear-filters-button"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-x"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
          <span>Clear Filters</span>
        </button>
      </div>
    </div>
  );
};

export default FilterBar;
